import React, { useState, useEffect, useRef } from 'react';
import { FiUsers, FiPlus, FiTrash2, FiSearch, FiChevronDown, FiBook, FiCalendar } from 'react-icons/fi';
import api from '../../utils/api';
import { formatIndianDateShort } from '../../utils/dateUtils';

const font = "'Outfit', sans-serif";
const C = { text: '#1e293b', sub: '#64748b', border: '#e2e8f0', bg: '#f8fafc', primary: '#059669', red: '#ef4444' };
const yearLabels = { '2001': '1st Year', '2002': '2nd Year', '2003': '3rd Year', '2004': '4th Year' };

// Reusable Searchable Dropdown
const SearchableDropdown = ({ label, placeholder, searchPlaceholder, items, value, displayValue, onSelect, renderItem, filterFn, disabled }) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const ref = useRef(null);

    useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const filtered = items.filter(item => filterFn(item, query));

    return (
        <div style={{ position: 'relative' }} ref={ref}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: C.sub, marginBottom: 4 }}>{label}</label>
            <div
                onClick={() => !disabled && setOpen(!open)}
                style={{ width: '100%', padding: '10px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14, fontFamily: font, background: disabled ? '#f1f5f9' : '#fff', cursor: disabled ? 'not-allowed' : 'pointer', minHeight: '40px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxSizing: 'border-box', opacity: disabled ? 0.6 : 1 }}
            >
                {value ? (
                    <span style={{ color: C.text }}>{displayValue}</span>
                ) : (
                    <span style={{ color: '#9ca3af' }}>{placeholder}</span>
                )}
                <FiChevronDown size={14} color="#9ca3af" />
            </div>

            {open && !disabled && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: `1px solid ${C.border}`, borderRadius: 8, marginTop: 4, zIndex: 20, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', maxHeight: 250, display: 'flex', flexDirection: 'column' }}>
                    <div style={{ padding: 8, borderBottom: `1px solid ${C.border}`, position: 'sticky', top: 0, background: '#fff', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <FiSearch size={14} color="#9ca3af" />
                        <input
                            type="text" placeholder={searchPlaceholder} value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            style={{ width: '100%', padding: '6px', border: `1px solid ${C.border}`, borderRadius: 4, fontSize: 13, fontFamily: font, boxSizing: 'border-box', outline: 'none' }}
                            autoFocus onClick={(e) => e.stopPropagation()}
                        />
                    </div>
                    <div style={{ overflowY: 'auto', maxHeight: 200 }}>
                        {filtered.length > 0 ? (
                            filtered.map((item, idx) => (
                                <div key={idx} onClick={() => { onSelect(item); setOpen(false); setQuery(''); }}
                                    style={{ padding: '10px 12px', fontSize: 14, cursor: 'pointer', borderBottom: `1px solid ${C.border}`, background: 'transparent', color: C.text }}
                                    onMouseEnter={(e) => e.currentTarget.style.background = '#f0fdf4'}
                                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                >{renderItem(item)}</div>
                            ))
                        ) : (
                            <div style={{ padding: 12, fontSize: 13, color: '#9ca3af', textAlign: 'center' }}>No results found</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

const AdminTeacherAssignments = () => {
    const [assignments, setAssignments] = useState([]);
    const [teachers, setTeachers] = useState([]);
    const [schedules, setSchedules] = useState([]);
    const [loading, setLoading] = useState(true);

    const [selectedSchedule, setSelectedSchedule] = useState(null);
    const [selectedTeacher, setSelectedTeacher] = useState('');
    const [selectedSubject, setSelectedSubject] = useState(null);

    const [submitting, setSubmitting] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => { fetchData(); }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [assData, techData, schedData] = await Promise.all([
                api.get('/api/admin/teacher-assignments'),
                api.get('/api/admin/teachers'),
                api.get('/api/admin/exam-schedules'),
            ]);
            setAssignments(assData.data.assignments || []);
            setTeachers(techData.data.teachers || []);
            setSchedules(schedData.data.schedules || []);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(''); setSuccess('');
        if (!selectedSchedule || !selectedTeacher || !selectedSubject) {
            setError('Please select exam schedule, teacher, and subject');
            return;
        }
        setSubmitting(true);
        try {
            await api.post('/api/admin/teacher-assignments', {
                teacher_username: selectedTeacher,
                subject_code: selectedSubject.subject_code,
                subject_name: selectedSubject.subject_name,
                batch_name: selectedSchedule.batch || null,
                department: selectedSchedule.department,
                semester: selectedSchedule.year || null,
            });
            setSuccess('Teacher assigned successfully!');
            fetchData();
            setSelectedTeacher('');
            setSelectedSubject(null);
        } catch (err) {
            setError(err.response?.data?.message || 'Error creating assignment');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Remove this assignment?')) return;
        try {
            await api.delete(`/api/admin/teacher-assignments/${id}`);
            fetchData();
        } catch { alert('Error deleting assignment'); }
    };

    // Get subjects from selected schedule
    const scheduleSubjects = selectedSchedule?.subjects || [];

    return (
        <div style={{ padding: '0px 10px', fontFamily: font, maxWidth: 1200, margin: '0 auto' }}>
            <div style={{ marginBottom: 24 }}>
                <h1 style={{ fontSize: 24, fontWeight: 800, color: C.text, margin: '0 0 4px' }}>Assign Teachers</h1>
                <p style={{ margin: 0, color: C.sub, fontSize: 14 }}>Select an exam schedule, then assign teachers to subjects.</p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: window.innerWidth < 768 ? '1fr' : 'minmax(300px, 420px) 1fr', gap: 24, alignItems: 'start' }}>
                {/* Form */}
                <div style={{ background: '#fff', borderRadius: 16, border: `1px solid ${C.border}`, padding: 20, boxShadow: '0 4px 12px rgba(0,0,0,0.03)', overflow: 'visible', paddingBottom: 40 }}>
                    <h2 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 16px', display: 'flex', alignItems: 'center', gap: 8 }}><FiPlus color={C.primary} /> New Assignment</h2>
                    {error && <div style={{ padding: 10, background: '#fef2f2', color: '#b91c1c', borderRadius: 8, fontSize: 13, marginBottom: 14 }}>{error}</div>}
                    {success && <div style={{ padding: 10, background: '#ecfdf5', color: '#047857', borderRadius: 8, fontSize: 13, marginBottom: 14 }}>{success}</div>}

                    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                        {/* 1. Select Exam Schedule */}
                        <SearchableDropdown
                            label="Select Exam Schedule *"
                            placeholder="-- Choose an exam schedule --"
                            searchPlaceholder="Search by department or batch..."
                            items={schedules}
                            value={selectedSchedule ? selectedSchedule.id : ''}
                            displayValue={selectedSchedule ? selectedSchedule.title : ''}
                            onSelect={(sched) => { setSelectedSchedule(sched); setSelectedSubject(null); }}
                            filterFn={(s, q) => {
                                const ql = q.toLowerCase();
                                return (s.title || '').toLowerCase().includes(ql) ||
                                    (s.department || '').toLowerCase().includes(ql) ||
                                    (s.batch || '').toLowerCase().includes(ql);
                            }}
                            renderItem={(s) => (
                                <>
                                    <div style={{ fontWeight: 600 }}>{s.title}</div>
                                    <div style={{ fontSize: 12, color: C.sub, marginTop: 2 }}>
                                        {s.subjects?.length || 0} subjects · {s.enrolled_students || 0} students
                                    </div>
                                </>
                            )}
                        />

                        {/* Info card when schedule selected */}
                        {selectedSchedule && (
                            <div style={{ background: '#e0f2fe', borderRadius: 10, padding: '12px 14px', border: '1px solid #bae6fd' }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: '#0369a1', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 6 }}>Schedule Info</div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, fontSize: 12, color: '#1e293b' }}>
                                    <div><strong>Dept:</strong> {selectedSchedule.department}</div>
                                    <div><strong>Year:</strong> {yearLabels[selectedSchedule.year] || selectedSchedule.year}</div>
                                    <div><strong>Batch:</strong> {selectedSchedule.batch || '—'}</div>
                                    <div><strong>Subjects:</strong> {selectedSchedule.subjects?.length || 0}</div>
                                </div>
                            </div>
                        )}

                        {/* 2. Select Teacher */}
                        <SearchableDropdown
                            label="Select Teacher *"
                            placeholder="-- Select Teacher --"
                            searchPlaceholder="Search by name or username..."
                            items={teachers}
                            value={selectedTeacher}
                            displayValue={teachers.find(t => t.username === selectedTeacher)?.name ? `${teachers.find(t => t.username === selectedTeacher).name} (${selectedTeacher})` : selectedTeacher}
                            onSelect={(t) => setSelectedTeacher(t.username)}
                            filterFn={(t, q) => (t.name || '').toLowerCase().includes(q.toLowerCase()) || (t.username || '').toLowerCase().includes(q.toLowerCase())}
                            renderItem={(t) => (
                                <>
                                    <div style={{ fontWeight: 500 }}>{t.name}</div>
                                    <div style={{ fontSize: 12, color: C.sub, marginTop: 2 }}>{t.username} · {t.department}</div>
                                </>
                            )}
                        />

                        {/* 3. Select Subject from schedule */}
                        <SearchableDropdown
                            label="Select Subject *"
                            placeholder={selectedSchedule ? '-- Select Subject --' : 'Select exam schedule first'}
                            searchPlaceholder="Search subject..."
                            items={scheduleSubjects}
                            value={selectedSubject ? selectedSubject.subject_code : ''}
                            displayValue={selectedSubject ? `${selectedSubject.subject_code} - ${selectedSubject.subject_name}` : ''}
                            disabled={!selectedSchedule}
                            onSelect={(s) => setSelectedSubject(s)}
                            filterFn={(s, q) => (s.subject_code || '').toLowerCase().includes(q.toLowerCase()) || (s.subject_name || '').toLowerCase().includes(q.toLowerCase())}
                            renderItem={(s) => (
                                <>
                                    <div style={{ fontWeight: 500 }}>{s.subject_name}</div>
                                    <div style={{ fontSize: 12, color: C.sub, marginTop: 2 }}>{s.subject_code}{s.exam_date ? ` · ${formatIndianDateShort(s.exam_date)}` : ''}{s.exam_time ? ` · ${s.exam_time}` : ''}</div>
                                </>
                            )}
                        />

                        <button type="submit" disabled={submitting} style={{ background: C.primary, color: '#fff', border: 'none', padding: 12, borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: submitting ? 'not-allowed' : 'pointer', marginTop: 8, fontFamily: font, opacity: submitting ? 0.7 : 1 }}>
                            {submitting ? 'Assigning...' : 'Assign Teacher'}
                        </button>
                    </form>
                </div>

                {/* Assignments List */}
                <div style={{ background: '#fff', borderRadius: 16, border: `1px solid ${C.border}`, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
                    <div style={{ padding: '16px 20px', background: C.bg, borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                        <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}><FiUsers color={C.primary} /> Current Assignments</h2>
                        <span style={{ fontSize: 12, background: '#e2e8f0', padding: '2px 8px', borderRadius: 20, fontWeight: 600 }}>
                            {searchQuery
                                ? `${assignments.filter(a => { const q = searchQuery.toLowerCase(); return (a.teacher_name||'').toLowerCase().includes(q)||(a.teacher_username||'').toLowerCase().includes(q)||(a.subject_name||'').toLowerCase().includes(q)||(a.subject_code||'').toLowerCase().includes(q)||(a.batch_name||'').toLowerCase().includes(q)||(a.department||'').toLowerCase().includes(q); }).length} / ${assignments.length}`
                                : assignments.length}
                        </span>
                    </div>

                    {/* Search bar */}
                    <div style={{ padding: '12px 16px', borderBottom: `1px solid ${C.border}`, background: '#fff' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: C.bg, borderRadius: 10, border: `1.5px solid ${C.border}`, padding: '8px 14px' }}>
                            <FiSearch size={14} color={C.sub} style={{ flexShrink: 0 }} />
                            <input
                                type="text"
                                placeholder="Search teacher, subject, batch, department..."
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                                style={{ border: 'none', outline: 'none', fontSize: 13, fontFamily: font, flex: 1, color: C.text, background: 'transparent' }}
                            />
                            {searchQuery && (
                                <button onClick={() => setSearchQuery('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.sub, fontSize: 18, lineHeight: 1, padding: 0 }}>×</button>
                            )}
                        </div>
                    </div>

                    {loading ? (
                        <div style={{ padding: 40, textAlign: 'center', color: C.sub }}>Loading...</div>
                    ) : assignments.length === 0 ? (
                        <div style={{ padding: 40, textAlign: 'center', color: C.sub }}>No teachers assigned yet.</div>
                    ) : (() => {
                        const q = searchQuery.toLowerCase();
                        const filtered = assignments.filter(a =>
                            !q ||
                            (a.teacher_name||'').toLowerCase().includes(q) ||
                            (a.teacher_username||'').toLowerCase().includes(q) ||
                            (a.subject_name||'').toLowerCase().includes(q) ||
                            (a.subject_code||'').toLowerCase().includes(q) ||
                            (a.batch_name||'').toLowerCase().includes(q) ||
                            (a.department||'').toLowerCase().includes(q)
                        );
                        if (filtered.length === 0) return (
                            <div style={{ padding: 40, textAlign: 'center', color: C.sub }}>No assignments match "{searchQuery}".</div>
                        );
                        return (
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                                <thead>
                                    <tr style={{ borderBottom: `2px solid ${C.border}` }}>
                                        <th style={{ padding: '12px 20px', fontSize: 12, fontWeight: 700, color: C.sub, textTransform: 'uppercase' }}>Teacher</th>
                                        <th style={{ padding: '12px 20px', fontSize: 12, fontWeight: 700, color: C.sub, textTransform: 'uppercase' }}>Subject</th>
                                        <th style={{ padding: '12px 20px', fontSize: 12, fontWeight: 700, color: C.sub, textTransform: 'uppercase' }}>Batch</th>
                                        <th style={{ padding: '12px 20px', fontSize: 12, fontWeight: 700, color: C.sub, textTransform: 'uppercase' }}>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.map(a => (
                                        <tr key={a.id} style={{ borderBottom: `1px solid ${C.border}` }}>
                                            <td style={{ padding: '14px 20px' }}>
                                                <div style={{ fontWeight: 600, fontSize: 14 }}>{a.teacher_name}</div>
                                                <div style={{ fontSize: 12, color: C.sub }}>@{a.teacher_username}</div>
                                            </td>
                                            <td style={{ padding: '14px 20px' }}>
                                                <div style={{ fontWeight: 600, fontSize: 14 }}>{a.subject_name}</div>
                                                <div style={{ fontSize: 12, color: C.sub }}>{a.subject_code}</div>
                                            </td>
                                            <td style={{ padding: '14px 20px' }}>
                                                <div style={{ fontWeight: 600, fontSize: 14 }}>{a.batch_name}</div>
                                                <div style={{ fontSize: 12, color: C.sub }}>{a.department}</div>
                                            </td>
                                            <td style={{ padding: '14px 20px' }}>
                                                <button onClick={() => handleDelete(a.id)} style={{ padding: 6, borderRadius: 6, background: '#fee2e2', color: C.red, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <FiTrash2 size={16} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        );
                    })()}
                </div>
            </div>
        </div>
    );
};

export default AdminTeacherAssignments;
